<template>
  <PrmDialog
    modal
    :draggable="false"
    :visible="visible"
    :closable="closable"
    :header="title"
    :close-on-escape="closeOnEscape"
    :maximizable="maximizable"
    :style="{
      width,
      maxWidth: maxWidth
        ? (maximized ? '100dvw' : maxWidth)
        : undefined,
    }"
    @update:visible="emit('update:visible', $event);"
    @hide="emit('close');"
    @show="emit('show');"
    @maximize="maximized = true"
    @unmaximize="maximized = false"
  >
    <template #maximizeicon>
      <MaximiseIcon v-if="!maximized" />
      <MinimiseIcon v-else />
    </template>
    <template
      v-if="!title"
      #header
    >
      <slot name="header">
&nbsp;
      </slot>
    </template>
    <template
      v-if="!!$slots.actions && hasSlotContent($slots.actions)"
      #footer
    >
      <div class="modal-qmulus__actions">
        <slot name="actions" />
      </div>
    </template>
    <template #default>
      <slot />
    </template>
  </PrmDialog>
</template>

<script setup lang="ts">
import PrmDialog from '@NitrexGroup/primevue/dialog';
import {
  CSSProperties, shallowRef,
} from 'vue';

import MaximiseIcon from '../../assets/maximise.svg';
import MinimiseIcon from '../../assets/minimise.svg';
import {
  hasSlotContent,
} from '../../utils/vue';

withDefaults(
  defineProps<{
    visible?: boolean,
    title?: string,
    closable?: boolean,
    maximizable?: boolean,
    width?: CSSProperties['width'],
    maxWidth?: CSSProperties['maxWidth'],
    /**
     * If set to true, the modal will close once the user presses the `Esc` button.
     * Default: false.
     */
    closeOnEscape?: boolean,
  }>(),
  {
    closable: true,
    // We deactivate the closing via 'esc' as this is in most cases undesired:
    // Allowing this bears the risk for the user to lose the progress and data.
    // So for certain modals the developer may opt-in for this potential destructive
    // feature when desired => Possibility to lose progress/data is low.
    closeOnEscape: false,
  },
);

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void,
  (e: 'close'): void,
  (e: 'show'): void,
}>();

defineSlots<{
  default(): any,
  actions?(): any,
  header?(): any,
}>();

const maximized = shallowRef(false);
</script>

<style scoped lang="scss">
.modal-qmulus {
  &__actions {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    gap: var(--gap);
  }
}
</style>
